import './index.css'

import cn from 'clsx'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { TRACK_PLAYLISTED_FROM_SIM_SEARCH } from '@disco/constants/EventTypes'
import {
  AGGREGATED_SEARCH,
  BROWSE_LIBRARIES,
  SHAREABLE_CHANNELS,
  SIM_SEARCH,
  TRACK_NESTING,
} from '@disco/constants/FeatureTypes'
import {
  BRIEF_INBOX,
  BRIEFS,
  CUSTOM_CHANNEL,
  INBOX_TYPES,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import { ARTIST, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  ARTIST_PANE,
  BRIEF_LIST_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
} from '@disco/constants/PaneTypes'
import {
  PLAYLIST_DATE_SORT,
  SORT_DIRECTION_DESC,
  TITLE_SORT,
  TRACK_DATE_SORT,
} from '@disco/constants/SortTypes'
import {
  ALBUM_VIEW,
  BRIEF_ALL_VIEW,
  DEFAULT_VIEW,
  TRACK_VIEW,
} from '@disco/constants/ViewTypes'
import * as ExplorerActions from '@disco/state-management/actions/ExplorerActions'
import * as GroupActions from '@disco/state-management/actions/GroupActions'
import { addNestedTracksToMain } from '@disco/state-management/actions/NestingActions'
import { updatePlaylistTags } from '@disco/state-management/actions/playlist-actions'
import { updateSearchParams } from '@disco/state-management/actions/SearchActions'
import * as SelectionActions from '@disco/state-management/actions/SelectionActions'
import {
  deletePlaylist,
  deleteTrack,
} from '@disco/state-management/actions/ServerActions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import {
  uploadFilesToMainTrack,
  uploadFilesToPlaylist,
} from '@disco/state-management/actions/upload-actions'
import * as UserActions from '@disco/state-management/actions/UserActions'
import { getBriefProject } from '@disco/state-management/reducers/entities/briefProjects'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { makeGetFeatureData } from '@disco/state-management/selectors/business'
import { getGroup } from '@disco/state-management/selectors/groups'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import {
  getConstanceSettings,
  getCustomFieldsConfig,
  getMultiLevelSetting,
  isNativeTagsEnabled,
} from '@disco/state-management/selectors/user'
import { getRelativeTime } from '@disco/utils/date'
import { sendAccessEvent } from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getDepthOf, getGroupName } from '@disco/utils/groups'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { plural } from '@disco/utils/string-utils'
import { getTrackMetadataFieldName } from '@disco/utils/track-metadata'
import {
  getNestedTracksCount,
  isAggregatedSearchTrack,
  isReadOnlyTrack,
} from '@disco/utils/tracks'

import type { Artist, ArtistId } from 'types/artist'
import type { BriefProject } from 'types/brief'
import type { CustomFieldsConfig } from 'types/business'
import type { ExplorerPane as TExplorerPane, ParentProps } from 'types/explorer'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import type { User } from 'types/user'

import { PaneProvider } from 'contexts/pane'

import { getTrackField } from 'utils/track-metadata'

import BriefDropWrapper from 'views/containers/BriefDropWrapper'

import ArtistInfo from 'components/common/artist-info'
import ClipboardInput from 'components/common/clipboard-input'
import NativeTagsPane from 'components/common/native-tags-pane'
import PlaylistMessage from 'components/common/playlists/message'
import ActionMenu from 'components/explorer/action-menu'
import PlaylistPaneHeader from 'components/explorer/pane/playlist-pane/header'
import TagList from 'components/explorer/pane/tag-list'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import DropConfirmationDialog from 'components/modals/dialogs/drop-confirmation-dialog'
import PlaylistActions from 'components/playlist-actions'
import TrackActions from 'components/track-actions'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Flex from 'components/ui/flex'
import Icon from 'components/ui/icon'
import Lozenge from 'components/ui/lozenge'
import { NativeScrollbars } from 'components/ui/scrollbar'
import Skeleton from 'components/ui/skeleton'
import TextOverflow from 'components/ui/text-overflow'
import Tooltip from 'components/ui/tooltip'
import Video from 'components/ui/video'
import Error from 'components/utils/error'
import Loading from 'components/utils/loading'

import PlaylistDropWrapper from 'containers/PlaylistDropWrapper'

import type { RootState } from 'store/configureStore'

import { BriefInboxPaneHeader } from 'features/briefs/components/brief-inbox-pane-header'
import BriefProjectActions from 'features/briefs/components/brief-project-actions'
import { NewBriefDropdown } from 'features/briefs/components/new-brief-dropdown'

import { PlaylistOrigin } from '../../common/playlists/origin'
import CopyInboxUrl from '../copy-inbox'
import MobilePaneSwitch from '../mobile-pane-switch'
import AggregatedSearch from './aggregated-search'
import ArtistPane from './artist-pane'
import EmptyList from './empty-list'
import ExplorerTrackVersions from './explorer-track-versions'
import LoadingMore from './loading-more'
import PaneHeader from './pane-header'
import PaneItems from './pane-items'
import PaneItemsSkeleton from './pane-items-skeleton'
import SearchPaneHeader from './search-header'
import { playlistOptions, trackOptions } from './search-header/select-options'
import ViewSelector from './search-header/view-selector'
import SubPane from './sub-pane'
import TrackDetails from './track-details'
import TrackTabs from './track-details/similarity-track-tabs'
import TrackPane from './track-pane'
import SimSearchNewPageBanner from './sim-search-new-page-banner'
import { Box, DiscoCallout, Text as DsText } from 'components/ds'

// don't create new arrays inside render methods
const DEFAULT_ITEMS = []
const SUBPANE_OPEN_KEY = 'explorer_subpane_open'

type Props = {
  loadNextPanePage: (...args: Array<any>) => any
  appendTracksToPlaylist: (...args: Array<any>) => any
  addNestedTracksToMain: (...args: Array<any>) => any
  uploadFilesToPlaylist: typeof uploadFilesToPlaylist
  uploadFilesToMainTrack: typeof uploadFilesToMainTrack
  updateUserSettingsUnsafe: (...args: Array<any>) => any
  switchPaneView: (...args: Array<any>) => any
  updatePlaylistTags: typeof updatePlaylistTags
  customFieldsConfig: CustomFieldsConfig
  componentHeight: 'Default' | 'Compact'
  isPhone: boolean
  pane: TExplorerPane
  prevPane: Record<string, any> | null | undefined
  groups: Group[]
  group?: Group
  groupUploadUrl?: string
  user: User
  colCount: number
  colWidth: number
  isSmall: boolean
  isSortingWide: boolean
  selectedItems: (Playlist | Track)[]
  style?: Record<string, any> | null | undefined
  tracks: Record<number, Track>
  playlists: Record<string, Playlist>
  library: Record<string, any>
  loadPane: (...args: Array<any>) => any
  searchSort: string
  searchSortDirection: string
  updateGroup: (...args: Array<any>) => any
  deleteGroup: (...args: Array<any>) => any
  updateSearchParams: (...args: Array<any>) => any
  shareableChannelsEnabled?: boolean
  simSearchEnabled?: boolean
  rightsDataEnabled?: boolean
  nativeTagsEnabled: boolean
  trackNestingEnabled: boolean
  aggregatedSearchEnabled?: boolean
  browseLibrariesEnabled?: boolean
  badgeTitle?: string
  isTrial: boolean
  artists: Record<ArtistId, Artist>
  isInboxEngagementEnabled: boolean
  constanceSettings: ReturnType<typeof getConstanceSettings>
  briefProject: BriefProject | null
}
type State = {
  loadingNextUrl: null | string
  dropConfirmationDialogOpen: boolean
  filterTags: string[]
  droppedItemFiles: File[] | null | undefined
  droppedItem: Record<string, any> | null | undefined
  dropTarget: Playlist | Track | null | undefined
  tabIndex: number
  subPaneOpen: boolean
}

class ExplorerPane extends React.Component<Props, State> {
  loadNext: (...args: Array<any>) => any
  _pane: HTMLDivElement | null | undefined

  constructor(props: Props) {
    super(props)
    this.state = {
      loadingNextUrl: null,
      dropConfirmationDialogOpen: false,
      filterTags: [],
      droppedItem: null,
      droppedItemFiles: null,
      dropTarget: null,
      tabIndex: 0,
      // TODO: Refactor component for useLocalStorage hook
      // Default value is true, because we want to show subpane by default
      subPaneOpen: window.localStorage.getItem(SUBPANE_OPEN_KEY) !== 'false',
    }
    this.loadNext = debounce(
      async () => {
        const { pane } = this.props
        if (!pane || pane.loading) return
        const nextUrl =
          pane.type === PLAYLIST_PANE
            ? get(pane, 'tracks_info.next', undefined)
            : pane.next

        if (this.state.loadingNextUrl !== nextUrl && nextUrl) {
          this.setState({
            loadingNextUrl: nextUrl,
          })
          await this.props.loadNextPanePage(this.props.pane)
          // reset "loadingNextUrl" after call, we don't really care if there was error, anyway url is not loading anymore
          this.setState({
            loadingNextUrl: null,
          })
        }
      },
      300,
      {
        leading: true,
      },
    )
  }

  componentDidMount() {
    sendAccessEvent(this.props.pane, this.props.groups)
  }
  _setSubPaneOpen = (subPaneOpen: boolean) => {
    window.localStorage.setItem(
      SUBPANE_OPEN_KEY,
      subPaneOpen ? 'true' : 'false',
    )
    this.setState({
      subPaneOpen,
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { type, groupId, loading, view, results, groupType } = nextProps.pane

    // Switch to track view if there are no results in the default/playlists view
    if (
      !loading &&
      type === SEARCH_RESULTS_PANE &&
      [TEAM_LIBRARY, PERSONAL_LIBRARY].includes(groupType) &&
      !view &&
      results &&
      results.length <= 0
    ) {
      this.props.switchPaneView(nextProps.pane, TRACK_VIEW, {
        sortParam: TRACK_DATE_SORT,
        sortDirection: SORT_DIRECTION_DESC,
        searchTerm: nextProps.pane.searchTerm,
      })
      return
    }

    // when user is switching pane to different content, pane will switch to loading state,
    // it's time to reset "loadingNextUrl" if it was set previously
    // cause next pages in groups urls can be equal(think second page, offset 50), and difference will be only in group headers
    // this can prevent the second page in the group from loading, cause previously user could start to load second page in the other group
    // and without waiting completion of the request, switch to another group, thus "loadingNextUrl" and "pane.next" will be equal,
    // and this will prevent all next pages in other groups from loading
    if (loading && !this.props.pane.loading) {
      this.setState({
        loadingNextUrl: null,
      })
    }

    if (
      type === SEARCH_RESULTS_PANE &&
      groupId &&
      groupId !== this.props.pane.groupId
    ) {
      this.setState({
        filterTags: [],
      })
    }

    if (nextProps.pane !== this.props.pane) {
      sendAccessEvent(nextProps.pane, nextProps.groups)
    }
  }

  // drop confirmation
  _onDrop() {
    return this.props.user.settings.hide_drop_warning
      ? undefined
      : this._showDropConfirmation
  }

  _showDropConfirmation = async (
    playlist,
    draggedItem:
      | {
          files: File[]
          items: any
        }
      | {
          track: Track
        },
  ) => {
    // We need to grab nested files from draggedItem here while we have access to the filesystem
    const droppedItemFiles: File[] | null | undefined =
      'items' in draggedItem ? await getFilesFromDraggedItem(draggedItem) : null
    if (droppedItemFiles?.length === 0) return
    this.setState({
      dropConfirmationDialogOpen: true,
      droppedItemFiles,
      droppedItem: draggedItem,
      dropTarget: playlist,
    })
  }

  _renderDropConfirmationDialog() {
    if (!this.state.dropConfirmationDialogOpen) return null
    const hideWarning = this.props.user.settings.hide_drop_warning || false
    return (
      <DropConfirmationDialog
        onConfirm={this._confirmDrop}
        onCancel={this._cancelDrop}
        hideWarning={hideWarning}
        onHideWarningChange={this._hideDropWarningChange}
      />
    )
  }

  _confirmDrop = () => {
    const { droppedItem, dropTarget, droppedItemFiles } = this.state

    if (dropTarget && dropTarget?.type === PLAYLIST) {
      if (droppedItemFiles) {
        this.props.uploadFilesToPlaylist(droppedItemFiles, dropTarget)
      } else if (droppedItem && droppedItem.track) {
        const trackSelected =
          this.props.selectedItems.length > 0 &&
          isDraggedItemPartOfSelection(this.props.selectedItems, droppedItem)

        if (!trackSelected) {
          this.props.appendTracksToPlaylist(dropTarget, [droppedItem.track])
        } else {
          const tracks = this.props.selectedItems.filter(
            item => item.type === TRACK,
          )

          if (tracks.length > 0) {
            this.props.appendTracksToPlaylist(dropTarget, tracks)
          }
        }
      }
    }

    this.setState({
      dropConfirmationDialogOpen: false,
      droppedItem: null,
      droppedItemFiles: null,
      dropTarget: null,
    })
  }
  _cancelDrop = () => {
    this.setState({
      dropConfirmationDialogOpen: false,
      droppedItem: null,
      dropTarget: null,
    })
  }
  _hideDropWarningChange = () => {
    const { settings } = this.props.user
    this.props.updateUserSettingsUnsafe({
      settings: {
        hide_drop_warning: !settings.hide_drop_warning,
      },
    })
  }

  _hasEnoughInfoForRightsDataQuery: (track: Track) => boolean = track => {
    if (track.metadata.ISRC) {
      return true
    }

    return Boolean(track.artist && track.name)
  }

  _renderAction(track: Record<string, any>) {
    const { pane, user, library, group, constanceSettings } = this.props
    const briefProjectsEnabled = constanceSettings?.BRIEF_PROJECTS || false

    if (pane.type === BRIEF_LIST_PANE) {
      if (pane.meta?.briefProjectId) {
        return <BriefProjectActions briefProjectId={pane.meta.briefProjectId} />
      }

      return (
        pane.loaded &&
        (briefProjectsEnabled ? (
          <NewBriefDropdown />
        ) : (
          <Button
            appearance="primary"
            caption="New Brief"
            compact
            link={'/brief-editor/new'}
          />
        ))
      )
    }

    if (INBOX_TYPES.includes(group?.group_type)) {
      const copyButton = group?.upload_url && (
        <CopyInboxUrl
          user={user}
          url={group?.upload_url}
          group={group}
          sendMixpanelEvent={(name, props) =>
            mixpanel.sendPureEvent(name, props)
          }
        />
      )
      return this._renderPaneHeader({
        copyButton,
      })
    }

    if (
      pane.groupType &&
      [CUSTOM_CHANNEL, SAVED_TO_DISCO].includes(pane.groupType)
    ) {
      return this._renderPaneHeader()
    }

    if (pane.type === SEARCH_RESULTS_PANE) {
      return this._renderPaneHeader()
    }

    if (pane.type === PLAYLIST_PANE) {
      return (
        <PlaylistActions
          playlist={
            pane.playlist_id
              ? {
                  playlist_id: pane.playlist_id,
                }
              : null
          }
          mixpanelContext="Playlist pane"
          hideShare={!pane?.permissions?.share_link}
        />
      )
    }

    if (pane.type === TRACK_PANE) {
      const sourcePlaylistId: number | null | undefined =
        this.props.prevPane?.playlist_id
      const index: number | null | undefined =
        this.props.prevPane?.activeItem?.split('/')[1]
      return (
        <TrackActions
          track={track as Track}
          group={library as Group}
          groupId={library && library.id}
          sourcePlaylistId={sourcePlaylistId}
          customEditMetadataUrl={
            sourcePlaylistId && index
              ? `/explorer/playlists/${sourcePlaylistId}/tracks/${index}/edit-metadata`
              : null
          }
          hideEls={['notes_shortcut']}
          allowSharing
          downloadTrack
          manageNesting
          manageTrackFormats
          editMetadata
          permissionsAndSharing
          addToShortlist={this.props.isPhone}
          deleteTrack
          addToCustomGroup
          addToNewCustomGroup
          copyMetadata
          mixpanelContext="Track pane"
        />
      )
    }
  }

  _getPropValue = (prop: string, track) => {
    return getTrackField(track, prop, this.props.customFieldsConfig)
  }
  _renderComments = (comments, label) =>
    comments &&
    comments.length > 0 && (
      <React.Fragment key={label}>
        <div className="ExplorerPane_metaname">
          {getTrackMetadataFieldName(label)}
        </div>
        {comments.map((item, index) => (
          <div className="ExplorerPane_metaval" key={index}>
            {item.comment}
          </div>
        ))}
      </React.Fragment>
    )
  _renderMeta = (prop, index, track) => {
    const value = this._getPropValue(prop, track)

    if (prop === 'Client comments' || prop === 'Internal comments') {
      let visibilityFilter
      if (prop === 'Client comments') visibilityFilter = 'client'
      if (prop === 'Internal comments') visibilityFilter = 'internal'
      const comments =
        track.comments &&
        track.comments.filter(c => c.visibility === visibilityFilter)
      return this._renderComments(comments, prop)
    }

    return (
      value && (
        <React.Fragment key={index}>
          <div className="ExplorerPane_metaname">{prop}</div>
          <div className="ExplorerPane_metaval">{value}</div>
        </React.Fragment>
      )
    )
  }
  _handleTabChange = (tabIndex: number) => {
    const SIM_SEARCH_TAB = 1

    if (this.props.simSearchEnabled && tabIndex === SIM_SEARCH_TAB) {
      mixpanel.sendPureEvent('Click on Track Panel Similar Tracks')
    }

    this.setState({
      tabIndex,
    })
  }
  _renderSaveInfo = (track: Track, readOnlyTrack: boolean) => {
    const { origin } = track
    if (!origin) return null
    const { primary_library, business, source_library } = origin
    const libraryLink = source_library
      ? source_library?.url
      : primary_library?.url
    const libraryName = source_library
      ? source_library?.name
      : primary_library?.name
    const contacts = primary_library?.contacts
    const businessName = business?.name
    return (
      <>
        {!readOnlyTrack && (primary_library || business || source_library) && (
          <>
            <div className="ExplorerPane_metaname">Saved from:</div>
            <div className="ExplorerPane_metaval">
              {libraryLink ? (
                <a
                  className="text-link"
                  href={libraryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={
                    source_library && libraryName
                      ? `${libraryName} - ${businessName}`
                      : businessName
                  }
                >
                  {source_library ? libraryName : businessName}
                </a>
              ) : (
                businessName
              )}
            </div>
          </>
        )}
        {contacts && (
          <>
            <div className="ExplorerPane_metaname">Contact:</div>
            <div className="ExplorerPane_metaval">
              {contacts.map((contact, index) => (
                <React.Fragment key={`contact-${index}-${contact.name}`}>
                  <a
                    className="text-link"
                    href={`mailto:${contact.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {contact.name}
                  </a>
                  {index !== contacts.length - 1 && <span>, </span>}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </>
    )
  }

  _renderPaneList(track?: Track) {
    const { pane } = this.props

    const list = this._renderList()

    if (pane.error) return list

    if (pane.type === PLAYLIST_PANE) {
      return (
        <PlaylistDropWrapper
          playlist={pane}
          appendTracksToPlaylist={this.props.appendTracksToPlaylist}
          uploadFilesToPlaylist={this.props.uploadFilesToPlaylist}
          selectedItems={this.props.selectedItems}
          onDrop={this._onDrop()}
        >
          {list}
        </PlaylistDropWrapper>
      )
    } else if (pane.type === ARTIST_PANE && pane.artistId) {
      return (
        <ArtistPane artistId={pane.artistId} pane={pane}>
          {list}
        </ArtistPane>
      )
    } else if (pane.type === TRACK_PANE) {
      if (!pane.loaded) return <Loading />
      if (!(pane.id && !Array.isArray(this.props.tracks))) return null
      if (!track) return null
      const paneFields = getMultiLevelSetting.explorer_trackpane_fields(
        this.props.user,
      )
      const playlistNum = track.playlists.length
      let editMetadataLink = `/explorer/tracks/${track.id}/edit-metadata`

      // This results in a crash if playlist was not properly saved (playlist_id is undefined) or
      // if track details pane was opened from search.
      // TODO: Probably search pane needs some other context to allow prev/next navigation from track editing modal.
      // Not sure if it is possible currently for search results.
      // TODO: Probability of undefined playlist_id needs further investigation.
      // Maybe this crash was caused only by opening track details from search.
      if (this.props.prevPane && this.props.prevPane.playlist_id) {
        const { playlist_id, activeItem } = this.props.prevPane
        const [, index] = activeItem.split('/')
        editMetadataLink = `/explorer/playlists/${playlist_id}/tracks/${index}/edit-metadata`
      }

      const renderMetadata = metadata =>
        metadata.map((prop, index) => this._renderMeta(prop, index, track))

      const readOnlyTrack = isReadOnlyTrack(track)

      const saveInfo = this._renderSaveInfo(track, readOnlyTrack)

      let metadataNode

      if (isEqual(paneFields.settingUser, paneFields.settingDefault)) {
        metadataNode = (
          <React.Fragment>
            <div className="ExplorerPane_meta">
              {saveInfo}
              {renderMetadata(paneFields.settingDefault)}
            </div>
            <div className="ExplorerPane_Message">
              <div className="ExplorerPane_Message_Icon">
                <Icon type="notification" width="24" height="24" />
              </div>
              <div className="ExplorerPane_Message_Content">
                You can choose the metadata fields you want to display{' '}
                <Link to="/settings/display">here</Link>.
              </div>
            </div>
          </React.Fragment>
        )
      } else {
        const metadataContents = renderMetadata(paneFields.settingUser)
        const hasVisibleMetadata = metadataContents.some(t => t)

        if (hasVisibleMetadata || saveInfo) {
          metadataNode = (
            <div className="ExplorerPane_meta">
              {saveInfo}
              {metadataContents}
            </div>
          )
        }
      }

      const parentProps: ParentProps = this.getParentProps()
      const count = getNestedTracksCount(parentProps.pane)
      const libraryId = isAggregatedSearchTrack(track)
        ? track.origin?.source_library?.id ?? null
        : null
      const showTrackVersions =
        this.props.trackNestingEnabled &&
        (count > 0 || parentProps.pane.nesting_parent_id)
      return (
        <>
          <TrackDetails
            track={track}
            action={track && this._renderAction(track)}
            playbackContext={{
              playlistId: this.props.prevPane?.playlist_id,
              groupId: this.props.library?.id,
              clientLibraryId: libraryId,
            }}
            prevPane={this.props.prevPane}
          />
          <div>
            {this.props.nativeTagsEnabled && (
              <NativeTagsPane track={track} readOnly={readOnlyTrack} />
            )}
            {metadataNode}
            {this.props.rightsDataEnabled &&
              this._hasEnoughInfoForRightsDataQuery(track) && (
                <Box px="2">
                  <DiscoCallout
                    mb="2"
                    style={{ marginBottom: '8px' }}
                    type="search"
                    variant="surface"
                  >
                    <DsText>
                      Additional rights information may be available for this
                      track.{' '}
                    </DsText>
                    <Link
                      color="blue"
                      to={`/explorer/tracks/${track.id}/rights-data`}
                    >
                      View rights data.
                    </Link>
                  </DiscoCallout>
                </Box>
              )}
            {track.origin?.source_library && (
              <div className="ExplorerPane_meta">
                <div className="ExplorerPane_metaname">From:</div>
                <div className="ExplorerPane_metaval">
                  <a
                    href={track.origin.source_library.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    onClick={() => {
                      // Properties for event #8 from https://disco-team.atlassian.net/browse/DISCO-5985
                      mixpanel.sendPureEvent('Click link', {
                        'Track ID': track.id,
                        Catalog: track.origin.source_library.id,
                        'Catalog Business': track.origin.business?.id,
                        Type: 'Originating catalog',
                        Context: 'Aggregated search',
                      })
                    }}
                  >
                    {track.origin.source_library.name}
                  </a>
                </div>
                <div className="ExplorerPane_metaname">Contact:</div>
                {track.origin.source_library.contacts.map(({ email }) => {
                  return (
                    <div className="ExplorerPane_metaval" key={email}>
                      {email}
                    </div>
                  )
                })}
              </div>
            )}

            {!this.props.isPhone && (
              <div
                className={cn('ExplorerPane_FullTrackInfo', {
                  'ExplorerPane_FullTrackInfo--WithVersions': showTrackVersions,
                })}
              >
                <Button
                  appearance="link"
                  caption="View full track info"
                  link={editMetadataLink}
                />
              </div>
            )}
          </div>

          {showTrackVersions && (
            <ExplorerTrackVersions parentProps={parentProps} />
          )}

          <>
            {this.props.simSearchEnabled &&
            !readOnlyTrack &&
            !INBOX_TYPES.includes(this.props.library?.group_type) ? (
              <TrackTabs
                onChange={this._handleTabChange}
                index={this.state.tabIndex}
                tabs={[
                  {
                    alias: 'alsoAppearsOn',
                    label: 'Also appears on',
                    content: {
                      playlistNum,
                      list,
                    },
                  },
                  {
                    isSimilarTrack: true,
                    alias: 'similarTracks',
                    label: 'Similar tracks',
                    content: {
                      simSearchProps: parentProps,
                      track: track,
                    },
                  },
                ]}
              />
            ) : (
              !readOnlyTrack && (
                <>
                  <div className="ExplorerPane_related-label-default">
                    Appears in {playlistNum}{' '}
                    {playlistNum === 1 ? (
                      <span>playlist</span>
                    ) : (
                      <span>playlists</span>
                    )}
                  </div>
                  <div className="ExplorerPane_related-cont">{list}</div>
                </>
              )
            )}
          </>
          {this.props.aggregatedSearchEnabled &&
            this.props.browseLibrariesEnabled &&
            this.props.simSearchEnabled &&
            readOnlyTrack && (
              <TrackTabs
                onChange={this._handleTabChange}
                index={this.state.tabIndex}
                tabs={[
                  {
                    isSimilarTrack: true,
                    isAggregatedTrack: true,
                    alias: 'similarTracks',
                    label: 'Similar tracks on DISCO',
                    content: {
                      simSearchProps: parentProps,
                      track: track,
                    },
                  },
                ]}
              />
            )}
        </>
      )
    } else if (pane.type === BRIEF_LIST_PANE) {
      return (
        <BriefDropWrapper
          project={pane.meta}
          selectedItems={this.props.selectedItems}
          pane={pane}
        >
          {list}
        </BriefDropWrapper>
      )
    } else {
      return list
    }
  }

  _renderWatermarkingStatusFlag(pane) {
    if (PLAYLIST === pane.type && pane.watermarking_status) {
      return (
        <div className="ExplorerPane_WatermarkingWrapper">
          <WatermarkingStatusFlag
            watermarking_status={pane.watermarking_status}
            type={pane.type}
          />
        </div>
      )
    }

    return null
  }

  hasSelection() {
    const hasSelection =
      this.props.selectedItems && this.props.selectedItems.length > 0
    return hasSelection
  }

  getParentProps() {
    const { pane } = this.props
    const parentProps: ParentProps = {
      loadNext: () => {},
      appendTracksToPlaylist: this.props.appendTracksToPlaylist,
      componentHeight: this.props.componentHeight,
      groups: this.props.groups,
      isLoading: Boolean(
        pane.next || (pane.tracks_info && pane.tracks_info.next),
      ),
      paneSelection: [],
      pane,
      selectedItems: this.props.selectedItems,
      library: this.props.library,
      addNestedTracksToMain: this.props.addNestedTracksToMain,
      uploadFilesToMainTrack: this.props.uploadFilesToMainTrack,
    }
    return parentProps
  }

  render() {
    const { subPaneOpen } = this.state
    const { pane, isSmall, isSortingWide, colWidth } = this.props
    const { type } = pane
    const track: Track | undefined = pane.id
      ? this.props.tracks[pane.id]
      : undefined
    const paneClass = cn(`e2col ExplorerPane is-${pane.type}`, {
      'has-active': this.props.pane.activeItem !== undefined,
      'is-small': isSmall,
      'is-sorting-wide': isSortingWide,
    })
    const body = (
      <div className="e2col-body">
        {this._renderPaneHead(track)}
        {this._renderMessage()}
        <div className="ExplorerPane_List" data-testid="explorer-pane-list">
          {this._renderPaneList(track)}
        </div>
      </div>
    )

    if (type === SEARCH_RESULTS_PANE && typeof pane.groupId !== 'number') {
      let videoEmbed = null
      const simSearch = pane?.simSearch
      const maxHeight = 300
      const aspect = 1.4
      let height = colWidth / aspect // Initial height based on aspect ratio

      let width = colWidth

      if (height > maxHeight) {
        height = maxHeight
        width = height * aspect // Adjust width based on the limited height
      }

      if (simSearch?.type === 'youtube') {
        videoEmbed = (
          <div className="Explorer_VideoContainer">
            <Video
              src={`https://www.youtube.com/embed/${simSearch.youtubeId}`}
              width={width}
              height={height}
            />
          </div>
        )
      }

      return (
        <PaneProvider
          value={{
            parentProps: this.getParentProps(),
          }}
        >
          <SubPane
            style={{ ...this.props.style, width: this.props.colWidth }}
            header={
              <>
                {videoEmbed}
                {simSearch?.url && (
                  <SimSearchNewPageBanner url={simSearch.url} />
                )}
                {this._renderPaneHead()}
                {this._renderPaneList()}
              </>
            }
            sticky={!subPaneOpen}
          >
            <AggregatedSearch
              subPaneOpen={subPaneOpen}
              setSubPaneOpen={this._setSubPaneOpen}
              renderSearchResults={(
                results,
                isLoading,
                handleLoadMoreClick,
                nextPageUrl,
              ) => {
                return this._renderItems(
                  results,
                  'aggregated',
                  [],
                  isLoading,
                  handleLoadMoreClick,
                  nextPageUrl,
                )
              }}
            />
          </SubPane>
        </PaneProvider>
      )
    }

    return (
      <div
        className={paneClass}
        data-testid="explorer-pane"
        style={{ ...this.props.style, width: this.props.colWidth }}
        ref={r => {
          this._pane = r
        }}
      >
        <PaneProvider
          value={{
            parentProps: this.getParentProps(),
          }}
        >
          <NativeScrollbars onScroll={this._handleScroll} dndScrolling>
            {body}
          </NativeScrollbars>
          {this._renderDropConfirmationDialog()}
        </PaneProvider>
      </div>
    )
  }

  mapSearchResultsToReducerObjects(
    results: Record<string, any>[] | null | undefined,
  ) {
    return (results || []).map(item => {
      switch (item.type) {
        case ARTIST:
          return { ...this.props.artists[item.id], type: ARTIST }

        case TRACK:
          return this.props.tracks[item.id]

        case PLAYLIST:
          return this.props.playlists[item.playlist_id]

        case GROUP:
          const group = this.props.groups.find(g => g.id === item.id)
          return { ...group, type: item.type }

        default:
          return item
      }
    })
  }

  _renderList() {
    const { loaded, loading, error, type, actionStatus, searchTerm } =
      this.props.pane
    const paneActionInProgress =
      actionStatus &&
      (actionStatus.appending || actionStatus.sorting || actionStatus.saving)

    if (loaded && !error) {
      let items
      let sections
      let loadNext

      // TODO: pass ids instead of objects and render them inside, this likely
      // will break compatiblity with stashed threads, for now I map full objects
      // to reducer objects
      switch (type) {
        case ARTIST_PANE:
        case SEARCH_RESULTS_PANE:
          items = this.mapSearchResultsToReducerObjects(this.props.pane.results)
          break

        case PLAYLIST_PANE:
          // ensure that object from reducer is being used
          const { playlist_id } = this.props.pane
          if (!playlist_id) break
          const playlist = this.props.playlists[String(playlist_id)]
          items = playlist?.tracks
            ? // @ts-expect-error
              playlist.tracks.map(trackId => this.props.tracks[trackId])
            : DEFAULT_ITEMS
          sections = playlist?.sections || DEFAULT_ITEMS
          break

        case TRACK_PANE:
          return (
            <TrackPane
              pane={this.props.pane}
              onDrop={this._onDrop()}
              library={this.props.library}
            />
          )

        case BRIEF_LIST_PANE:
          items = this.props.pane.results
          loadNext = this.props.loadNextPanePage
          break

        default:
          return null
      }

      return (
        // TODO: ensure all parents of AutoSizer got height: '100%'; move to css
        <div
          style={
            this.props.pane.type === SEARCH_RESULTS_PANE
              ? {
                  height: '100%',
                  overflowY: 'auto',
                }
              : undefined
          }
        >
          {this._renderSecondPaneHeader()}
          {this._renderBulkEditing(items)}
          {this._renderItems(
            items,
            'library',
            sections,
            Boolean(this.state.loadingNextUrl),
            loadNext,
            this.props.pane.next,
          )}
          {/* TODO: delete _renderMoreItems */}
          {this.props.pane.type !== SEARCH_RESULTS_PANE
            ? this._renderMoreItems()
            : null}
        </div>
      )
    } else if (
      loading &&
      type === SEARCH_RESULTS_PANE &&
      this.props.pane.results // results were set previously, this means that we are switching view
    ) {
      return (
        <div className="ExplorerPane_LoadingContainer">
          <Loading />
        </div>
      )
    } else if (loading || (error && paneActionInProgress)) {
      return (
        <div className="ExplorerPane_LoadingContainer">
          <Loading />
        </div>
      )
    } else if (error) {
      return (
        <Error
          error={error}
          retry={this.props.loadPane.bind(this)}
          pane={this.props.pane}
        />
      )
    }
  }

  _handleScroll = e => {
    const { scrollHeight, scrollTop, offsetHeight } = e.target
    if (scrollHeight - (scrollTop + offsetHeight) < 2000) this.loadNext(e)
  }
  _handleViewSwitch = (newView: string, newSortParam?: string) => {
    const { view, searchTerm, type } = this.props.pane

    if (!view && newView === DEFAULT_VIEW) return

    if (type === BRIEF_LIST_PANE) {
      this.props.switchPaneView(this.props.pane, newView || BRIEF_ALL_VIEW, {
        searchTerm,
      })
      return
    }

    // Event #2 from https://disco-team.atlassian.net/browse/DISCO-4710
    if (view !== newView && INBOX_TYPES.includes(this.props.pane.groupType)) {
      mixpanel.sendPureEvent('Filter view in explorer', {
        explorer_pane: 'inbox',
        type: newView === DEFAULT_VIEW ? 'playlists' : 'tracks',
      })
    }

    // Event #3 from https://disco-team.atlassian.net/browse/DISCO-4710
    if (newSortParam) {
      const sortItem =
        trackOptions.find(o => o.value === newSortParam) ||
        playlistOptions.find(o => o.value === newSortParam)

      if (sortItem?.label) {
        mixpanel.sendPureEvent('Sort view in explorer', {
          explorer_pane: 'inbox',
          type: sortItem.label,
        })
      }
    }

    if (view !== newView || newSortParam) {
      const { filterTags } = this.state

      if (newSortParam) {
        const paramArray = newSortParam.split(' ')
        const sortParamSwitch = paramArray
          .slice(0, paramArray.length - 1)
          .join(' ')
        const sortDirectionSwitch = paramArray[paramArray.length - 1]

        this.props.switchPaneView(this.props.pane, newView || DEFAULT_VIEW, {
          sortParam: sortParamSwitch,
          sortDirection: sortDirectionSwitch,
          tags: filterTags.length > 0 ? filterTags : null,
          searchTerm,
        })

        if (newView === DEFAULT_VIEW) {
          if (sortParamSwitch === PLAYLIST_DATE_SORT) {
            mixpanel.sendPureEvent('Sort playlists by date')
          } else if (sortParamSwitch === TITLE_SORT) {
            mixpanel.sendPureEvent('Sort playlists by name')
          }
        }
      } else {
        this.props.switchPaneView(this.props.pane, newView || DEFAULT_VIEW, {
          tags: filterTags.length > 0 ? filterTags : null,
          searchTerm,
        })
      }

      if (newView === TRACK_VIEW) {
        if (this.props.library.group_type === CUSTOM_CHANNEL) {
          mixpanel.sendPureEvent('Click Tracks toggle in channel')
        } else {
          mixpanel.sendPureEvent('Click Tracks toggle in explorer')
        }
      }
    }
  }

  _renderPaneHeader({ copyButton }: { copyButton?: React.ReactNode } = {}) {
    const group = this.props.groups.find(g => g.id === this.props.pane.groupId)
    const groupDepth = getDepthOf(this.props.groups, group)
    return (
      <SearchPaneHeader
        pane={this.props.pane as any}
        group={group}
        groupDepth={groupDepth}
        searchSort={this.props.searchSort}
        searchSortDirection={this.props.searchSortDirection}
        copyButton={copyButton}
        filterTags={this.state.filterTags}
        onTagAdd={this._onTagFilterAdd}
        onTagRemove={this._onTagFilterRemove}
        onViewSwitch={this._handleViewSwitch}
        isTrial={this.props.isTrial}
      />
    )
  }

  _renderSecondPaneHeader() {
    const { pane } = this.props
    const tags = this.state.filterTags
    if (tags.length === 0 || pane.view === TRACK_VIEW) return null
    // TODO: merge _renderSecondPaneHeader with `PlaylistPaneHeader` - same layout
    return (
      <div className="ExplorerPane_Header">
        <TagList tags={tags} onTagRemove={this._onTagFilterRemove} />
      </div>
    )
  }

  _renderItems(
    items,
    searchType: 'aggregated' | 'library',
    sections,
    isLoading = false,
    handleLoadMoreClick,
    nextPageUrl,
  ) {
    const { pane, simSearchEnabled, group, aggregatedSearchEnabled } =
      this.props

    const { type, simSearch } = pane
    const manualScroll =
      searchType === 'library' &&
      type === SEARCH_RESULTS_PANE &&
      typeof this.props.pane.groupId !== 'number' && // if groupId it's channel, not search
      aggregatedSearchEnabled

    // if aggregated search is disabled, we don't need manual scroll as there will be single pane in a column
    if (!items || !items.length) {
      if (
        isLoading ||
        (pane.searching && searchType !== 'aggregated' && simSearchEnabled)
      )
        return <PaneItemsSkeleton itemsCount={manualScroll ? 1 : undefined} />
      // aggregated search status is not tied to the pane (!), so we need to change it here
      return searchType === 'aggregated' ? (
        <EmptyList
          pane={{ ...this.props.pane, searching: isLoading }}
          tags={this.state.filterTags}
        />
      ) : (
        <EmptyList
          pane={this.props.pane}
          tags={this.state.filterTags}
          group={group}
        />
      )
    }

    let updatedItems = items

    if (simSearch) {
      updatedItems = items.map(item => ({
        ...item,
        internal_data: {
          ...(item.internal_data || {}),
          [TRACK_PLAYLISTED_FROM_SIM_SEARCH]: simSearch,
        },
      }))
    }

    return (
      <PaneItems // itemPrefix={this.props.pane.view}
        sections={sections}
        items={updatedItems} // disabled={searchType === 'aggregated'}
        searchType={searchType}
        manualScroll={manualScroll}
        itemPrefix={
          searchType === 'aggregated' ? 'aggregated' : this.props.pane.view
        }
        subPaneOpen={this.state.subPaneOpen}
        pane={this.props.pane}
        // @ts-expect-error check if this is still needed
        onDrop={this._onDrop()}
        shareableChannelsEnabled={this.props.shareableChannelsEnabled}
        isLoading={isLoading}
        componentHeight={this.props.componentHeight}
        loadNext={handleLoadMoreClick || this.loadNext}
        nextPageUrl={nextPageUrl}
        isSimilarity={Boolean(simSearch)}
        disableSelection={searchType === 'aggregated'}
        {...this.props}
      />
    )
  }

  _getPanePlaylist = (): Playlist | null => {
    const {
      playlists,
      pane: { playlist_id },
    } = this.props
    if (!playlist_id) return null
    return playlists[String(playlist_id)] || (this.props as any)
  }
  _getShare = () => {
    const playlist = this._getPanePlaylist()

    return playlist?.share
  }
  _handleCopyLink = url => {
    mixpanel.sendPureEvent('Copy inbox url', {
      Url: url,
    })
  }

  _renderMessage() {
    const { library, isInboxEngagementEnabled } = this.props
    if (
      !library ||
      !library.name ||
      INBOX_TYPES.indexOf(library.group_type) < 0
    )
      return null

    if (isInboxEngagementEnabled) {
      const { type, is_welcome_to_disco_playlist } = this.props.pane

      const playlist = this._getPanePlaylist()

      const share = this._getShare()

      if (type === 'playlist' && playlist) {
        const sender = share?.user_detail
          ? {
              name: share.user_detail.name,
              email: share.user_detail.email,
            }
          : undefined
        const message = share?.message
          ? {
              message: share.message,
            }
          : undefined
        const libraryUploadUrl = this.props.library.upload_url
        const libraryInternalName = this.props.library.internal_name

        const fromBrief =
          this.props.pane.meta?.briefId && this.props.pane.meta?.inboxId
            ? {
                id: this.props.pane.meta?.briefId,
                submission: {
                  version: playlist.brief_version_submitted_to,
                  versionCreated: playlist.version_created_at,
                  isLatestVersion:
                    playlist.latest_version ===
                    playlist.brief_version_submitted_to,
                },
                inboxId: this.props.pane.meta?.inboxId,
              }
            : undefined

        return (
          <PlaylistOrigin
            origin={playlist.origin}
            sender={sender}
            message={message}
            showFullMessage={is_welcome_to_disco_playlist}
            fromBrief={fromBrief}
            footer={
              is_welcome_to_disco_playlist && libraryUploadUrl ? (
                <ButtonContainer>
                  <ClipboardInput
                    successNotification={{
                      title: `${libraryInternalName} link copied!`,
                      message: 'The link has been copied to your clipboard.',
                      level: 'success',
                    }}
                    customCta={
                      <Button
                        appearance="primary"
                        caption="Copy Link"
                        onClick={() => this._handleCopyLink(libraryUploadUrl)}
                      />
                    }
                    value={libraryUploadUrl}
                  />
                  <Button
                    caption="Open Link"
                    linkTarget="_blank"
                    link={libraryUploadUrl}
                  />
                </ButtonContainer>
              ) : null
            }
          />
        )
      }
    } else {
      // TODO: remove https://disco-team.atlassian.net/browse/DISCO-6676
      const {
        pane: { playlist_id },
      } = this.props
      const { type, name } = this.props.pane
      if (!playlist_id) return null

      const share = this._getShare()

      if (type === 'playlist' && share && share.message) {
        return (
          <PlaylistMessage
            title={name}
            message={share.message}
            date={share.timestamp}
            user={share.user_detail}
          />
        )
      }
    }

    return null
  }

  _renderPaneHead(track?: Track) {
    const { pane, briefProject } = this.props
    let paneName = pane.name

    let isBriefsPane = pane.type === BRIEF_LIST_PANE
    if (isBriefsPane && pane.meta?.briefProjectId) {
      paneName = briefProject?.name
    } else if (isBriefsPane) {
      paneName = 'Briefs'
    } else if (pane.type === PLAYLIST) {
      paneName = paneName || 'Untitled playlist'
    }

    let paneCaption

    if (isBriefsPane && !pane.meta?.briefProjectId) {
      paneCaption = (
        <ViewSelector
          groupType={BRIEFS}
          value={
            pane.view || (!this.props.pane.loaded ? 'loading' : BRIEF_ALL_VIEW)
          }
          onChange={this._handleViewSwitch}
        />
      )
    } else if (
      pane.groupId &&
      ![CUSTOM_CHANNEL, SAVED_TO_DISCO].includes(pane.groupType)
    ) {
      paneCaption = (
        <ViewSelector
          groupType={pane.groupType}
          value={
            pane.view || (!this.props.pane.loaded ? 'loading' : DEFAULT_VIEW)
          }
          onChange={this._handleViewSwitch}
        />
      )
    }

    if (pane.groupType === BRIEF_INBOX) {
      return <BriefInboxPaneHeader pane={pane} />
    }

    if (pane.type === ARTIST_PANE) {
      return (
        <div className="ExplorerPane_Head">
          <div className="ExplorerPane_Head_Content">
            {pane.artistId && (
              <ArtistInfo artist={this.props.artists[pane.artistId]} />
            )}
          </div>
          <ButtonContainer>
            {this.hasSelection() && <ActionMenu />}
            {pane.artistId && (
              <Tooltip content="Edit profile">
                <Button
                  appearance="icon"
                  icon="edit"
                  link={{
                    pathname: `/explorer/artists/${pane.artistId}`,
                    state: {
                      modal: true,
                      returnTo: '/',
                    },
                  }}
                />
              </Tooltip>
            )}
          </ButtonContainer>
        </div>
      )
    }

    if (pane.type === PLAYLIST_PANE) {
      const showcaseType = pane.showcase
        ? `${pane.showcase.type.toUpperCase()} page`
        : null
      const trackCount = pane.tracks_info?.count ?? 0

      const playlist = this._getPanePlaylist()

      const share = this._getShare()

      let subtitle = `${plural(trackCount, 'track', 'tracks')}`
      const timestamp = share?.timestamp || playlist?.timestamp
      if (timestamp) subtitle += ` | ${getRelativeTime(timestamp)}`
      paneCaption = pane.loading ? (
        <Skeleton />
      ) : (
        <Flex spacing="small">
          {Boolean(showcaseType) && <Lozenge text={showcaseType} />}
          <Flex.Content>
            <TextOverflow content={subtitle} />
          </Flex.Content>
        </Flex>
      )
    }

    if (pane.type === TRACK_PANE && !this.props.isPhone) {
      return this.hasSelection() && <ActionMenu />
    }

    return (
      <PaneHeader
        isLoading={pane.type === PLAYLIST_PANE && pane.loading}
        title={paneName}
        groupId={pane.groupId}
        caption={
          <>
            {paneCaption}
            {this._renderWatermarkingStatusFlag(pane)}
          </>
        }
        pane={pane}
      >
        {this.props.isPhone && <MobilePaneSwitch />}
        <ButtonContainer>
          {this.hasSelection() && <ActionMenu />}
          {this._renderAction(track)}
        </ButtonContainer>
      </PaneHeader>
    )
  }

  _renderBulkEditing(items) {
    if (this.props.pane.type === PLAYLIST_PANE && items && items.length) {
      const {
        playlists,
        pane: { playlist_id },
      } = this.props
      if (!playlist_id) return null
      const playlist = playlists[String(playlist_id)]
      return (
        <PlaylistPaneHeader
          playlist={playlist}
          onTagAdd={this._onPlaylistTagAdd}
          onTagRemove={this._onPlaylistTagRemove}
        />
      )
    }
  }

  _onPlaylistTagAdd = tagName => {
    const { playlist_id } = this.props.pane
    if (!playlist_id) return
    const playlist = this.props.playlists[String(playlist_id)]
    this.props.updatePlaylistTags(playlist, [...playlist.tags, tagName])
    mixpanel.sendPureEvent('Add playlist tag in explorer')
  }
  _onPlaylistTagRemove = tagName => {
    const { playlist_id } = this.props.pane
    if (!playlist_id) return
    const playlist = this.props.playlists[String(playlist_id)]
    this.props.updatePlaylistTags(
      playlist,
      playlist.tags.filter(t => t !== tagName),
    )
  }
  _applyTagFilter = tagList => {
    this.setState({
      filterTags: tagList,
    })
    const { view, playlistSortDirection, playlistSortParam } = this.props.pane

    if (!view || [DEFAULT_VIEW, ALBUM_VIEW].includes(view)) {
      this.props.switchPaneView(this.props.pane, view || DEFAULT_VIEW, {
        sortParam: playlistSortParam,
        sortDirection: playlistSortDirection,
        tags: tagList.length > 0 ? tagList : null,
      })

      if (tagList.length > 0) {
        mixpanel.sendPureEvent(
          view === ALBUM_VIEW
            ? 'Filter albums by tag'
            : 'Filter playlists by tag',
        )
      }
    }
  }
  _onTagFilterRemove = tag => {
    const tagList = this.state.filterTags.filter(t => t !== tag)

    this._applyTagFilter(tagList)
  }
  _onTagFilterAdd = tag => {
    if (this.state.filterTags.indexOf(tag) > -1) return
    const tagList = [...this.state.filterTags, tag]

    this._applyTagFilter(tagList)
  }

  _renderMoreItems() {
    const { pane } = this.props
    const moreItems = pane.next || (pane.tracks_info && pane.tracks_info.next)
    if (moreItems) return <LoadingMore />
  }
}

export default connect(
  (state: RootState, props: Props) => {
    const getFeatureData = makeGetFeatureData()
    const { isFeatureEnabled, isTrial, badgeTitle } = getFeatureData(state, {
      featureName: SHAREABLE_CHANNELS,
    })
    const { isFeatureEnabled: isSimSearchEnabled } = getFeatureData(state, {
      featureName: SIM_SEARCH,
    })
    const { isFeatureEnabled: isBrowserLibrariesEnabled } = getFeatureData(
      state,
      {
        featureName: BROWSE_LIBRARIES,
      },
    )
    const { isFeatureEnabled: isAggregatedSearchEnabled } = getFeatureData(
      state,
      {
        featureName: AGGREGATED_SEARCH,
      },
    )
    const constanceSettings = getConstanceSettings(state)
    const rightsDataEnabled =
      constanceSettings?.THIRD_PARTY_RIGHTS_DATA || false
    const { isFeatureEnabled: isTrackNestingEnabled } = getFeatureData(state, {
      featureName: TRACK_NESTING,
    })
    // TODO: remove https://disco-team.atlassian.net/browse/DISCO-6676
    const { isFeatureEnabled: isInboxEngagementEnabled } = getFeatureData(
      state,
      {
        featureName: 'INBOX_ENGAGEMENT',
      },
    )
    return {
      artists: state.artists.items,
      groups: state.groups.groups,
      playlists: state.playlists,
      playlistTypes: state.entities.playlistTypes,
      customFieldsConfig: getCustomFieldsConfig(state),
      componentHeight: state.user.settings.explorer_component_height,
      explorer_trackpane_fields: getMultiLevelSetting.explorer_trackpane_fields(
        state.user,
      ).settingUser,
      searchSort: state.search.sort,
      searchSortDirection: state.search.sortDirection,
      tracks: state.tracks,
      selectedItems: getAllSelectedItems(state),
      isPhone: isDeviceTypePhone(state),
      shareableChannelsEnabled: isFeatureEnabled,
      simSearchEnabled: isSimSearchEnabled,
      rightsDataEnabled: rightsDataEnabled,
      aggregatedSearchEnabled: isAggregatedSearchEnabled,
      browseLibrariesEnabled: isBrowserLibrariesEnabled,
      nativeTagsEnabled: isNativeTagsEnabled(state),
      trackNestingEnabled: isTrackNestingEnabled,
      briefProject: getBriefProject(state, {
        briefProjectId: Number(props.pane.meta?.briefProjectId),
      }),
      isTrial,
      badgeTitle,
      group: getGroup(state, {
        groupId: props.pane.groupId,
      }),
      isInboxEngagementEnabled,
      constanceSettings: constanceSettings,
    }
  },
  {
    ...ExplorerActions,
    ...GroupActions,
    ...UserActions,
    ...SelectionActions,
    deletePlaylist,
    deleteTrack,
    createNewShortlist,
    updatePlaylistTags,
    updateSearchParams,
    uploadFilesToMainTrack,
    uploadFilesToPlaylist,
    addNestedTracksToMain,
  },
  // @ts-expect-error type connector
)(ExplorerPane) as any
